import axios from "axios";

import api from "../config/api";

import { ServiceReturn } from "../types/ServiceResponse";

import { AuthUser } from "../models/User";

import { LoginForm } from "../schemas/loginSchema";
import { normalizeAuthUser } from "./authUser";
import { clearSessionTokens, getSessionAccessToken, refreshSessionTokens, setSessionTokens } from "./sessionToken";

const moduleBaseUrl = "auth";
const centralAuthStateKey = "VoidChat@AuthState";
const centralAuthClientId = "VOID_CHAT";
const voidAuthBaseURL = () => process.env.REACT_APP_VOID_AUTH_API_URL?.replace(/\/+$/, "") ?? "";

type VoidAuthLoginResponse = {
  data?: {
    accessToken?: string;
    refreshToken?: string;
  };
  message?: string;
};

type AuthServiceReturn = ServiceReturn<AuthUser> & {
  productAccessUnavailable?: boolean;
};

let codeExchangeInFlight: { code: string; state: string; promise: Promise<ServiceReturn<AuthUser>> } | null = null;

export const auth = async ( loginForm: LoginForm ): Promise<ServiceReturn<AuthUser>> => {
  if( voidAuthBaseURL()) {
    redirectToCentralAuth();
    return { data: false, message: "Redirecionando para o Void Auth" };
  }
  return { data: false, message: "Void Auth central nao configurado" };
};

export const isCentralAuthEnabled = () => Boolean( voidAuthBaseURL());

const callbackURL = () => `${ window.location.origin }/auth/callback`;

const randomState = () => {
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("") || `${ Date.now() }`;
};

export const redirectToCentralAuth = () => {
  const authBaseURL = voidAuthBaseURL();
  if( !authBaseURL ) return;
  const state = randomState();
  window.sessionStorage.setItem( centralAuthStateKey, state );
  const url = new URL(`${ authBaseURL }/authorize`);
  url.searchParams.set("client_id", centralAuthClientId);
  url.searchParams.set("redirect_uri", callbackURL());
  url.searchParams.set("state", state);
  window.location.assign( url.toString());
};

const isProductAccessUnavailableStatus = ( status?: number ) => status === 401 || status === 403;

export const exchangeCentralAuthCallback = async ( code: string, state: string ): Promise<AuthServiceReturn> => {
  if( codeExchangeInFlight && codeExchangeInFlight.code === code && codeExchangeInFlight.state === state ) {
    return await codeExchangeInFlight.promise;
  }

  try {
    const expectedState = window.sessionStorage.getItem( centralAuthStateKey );
    window.sessionStorage.removeItem( centralAuthStateKey );
    if( !expectedState || expectedState !== state ) {
      return { data: false, message: "Estado de autenticacao invalido" };
    }

    codeExchangeInFlight = {
      code,
      state,
      promise: axios.post<VoidAuthLoginResponse>(`${ voidAuthBaseURL() }/v1/auth/code/exchange`, {
        clientId: centralAuthClientId,
        redirectUri: callbackURL(),
        code,
      }).then(async ( loginResponse ) => {
        const accessToken = loginResponse.data?.data?.accessToken;
        const refreshToken = loginResponse.data?.data?.refreshToken;
        if( !accessToken ) return { data: false, message: "Falha ao autenticar" };

        setSessionTokens( accessToken, refreshToken );

        let data;
        try {
          const response = await api.get(`/${ moduleBaseUrl }/me`, {
            headers: { Authorization: `Bearer ${ accessToken }` },
          });
          data = response.data;
        } catch ( error: any ) {
          clearSessionTokens();
          return {
            data: false,
            message: error?.response?.data?.message ?? error?.message,
            productAccessUnavailable: isProductAccessUnavailableStatus( error?.response?.status ),
          };
        }

        if( data?.data ) {
          data.data = normalizeAuthUser({
            ...data.data,
            token: accessToken,
          });
        }

        return data;
      }).finally(() => {
        codeExchangeInFlight = null;
      })
    };

    return await codeExchangeInFlight.promise;
  } catch ( error: any ) {
    clearSessionTokens();
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getAuthenticatedUser = async (): Promise<AuthServiceReturn> => {
  try {
    const accessToken = getSessionAccessToken();
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }/me`, accessToken ? {
      headers: { Authorization: `Bearer ${ accessToken }` },
    } : undefined );

    if( typeof data?.data !== "object" || !data.data ) {
      return { data: false, message: "Resposta inválida de /auth/me. Verifique REACT_APP_API_URL.", productAccessUnavailable: false };
    }

    data.data = normalizeAuthUser({
      ...data.data,
      token: data.data.token ?? accessToken,
    });

    return data;
  } catch ( error: any ) {
    const hadCentralSession = Boolean( getSessionAccessToken()) && isCentralAuthEnabled();
    const productAccessUnavailable = hadCentralSession && isProductAccessUnavailableStatus( error?.response?.status );
    if( productAccessUnavailable ) clearSessionTokens();
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
      productAccessUnavailable,
    };
  }
};

export const logoutAuthenticatedUser = async (): Promise<ServiceReturn<boolean>> => {
  let accessToken = getSessionAccessToken();

  const authBaseURL = voidAuthBaseURL();
  const logoutFromCentralAuth = async ( token: string ) => axios.post(`${ authBaseURL }/v1/auth/logout`, undefined, {
    headers: { Authorization: `Bearer ${ token }` },
    withCredentials: true,
  });

  try {
    if( authBaseURL && accessToken ) {
      try {
        await logoutFromCentralAuth( accessToken );
      } catch ( error: any ) {
        if( error?.response?.status !== 401 ) throw error;
        const refreshed = await refreshSessionTokens();
        accessToken = refreshed.accessToken;
        await logoutFromCentralAuth( accessToken );
      }
    }

    return { data: true, message: "" };
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  } finally {
    clearSessionTokens();
  }
};
